import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import LanguageItem from '../LanguageItem/LanguageItem';
import SearchComponent from '../../../../components/SearchComponent/SearchComponent';
import styles from './SubtitlesEditorComponent.module.scss';
import cx from 'classnames';
import { CustomButton } from '../../../../components/Button/Button';
import { useTranslation } from 'react-i18next';
import LanguageSelect from '../LanguageSelect/LanguageSelect';
import SubtitlesLanguageCheckbox from '../SubtitlesLanguageCheckbox/SubtitlesLanguageCheckbox';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import Images from '../../../../constants/Images';
import CmsAPI from '../../../../../common/apis/CmsAPI';
import { EDITOR_MODE, VOICEOVER_COVERAGE_TYPES } from '../../const';
import SubtitlesItemsList from '../SubtitlesItemsList/SubtitlesItemsList';
import SubtitlesViewTabs from '../SubtitlesViewTabs/SubtitlesViewTabs';
import { CLIPS_TYPE, VOICEOVER_TYPE } from '../../../../constants/clipsConstants';

const SubtitlesEditorComponent = ({
  mode,
  subtitlesLanguage,
  subtitles,
  updateSubtitle,
  activeSubtitle,
  searchValue,
  onSearch,
  availableLanguages,
  currentLanguage,
  onLanguageChange,
  handleSeekToSubtitle,
  onSearchClose,
  video,
  listMode,
  setListMode,
}) => {
  const [isSearching, setIsSearching] = useState(false);
  const [showOptions, setShowOption] = useState(false);
  const { t } = useTranslation();
  const [subtitlesVisibilityChanged, setSubtitlesVisibilityChanged] = useState(false);

  const isVoiceoverSubtitleExist = useMemo(() => subtitles[VOICEOVER_TYPE]?.length > 0, [subtitles]);

  const [subtitlesInPlayer, setSubtitlesInPlayer] = useState([]);

  const amountAvailableLanguages = useMemo(() => subtitlesInPlayer.filter(subtitles => subtitles?.visible).length, [
    subtitlesInPlayer,
  ]);

  const isTranslation = useMemo(() => subtitlesLanguage?.id !== currentLanguage?.id, [subtitlesLanguage, currentLanguage]);
  const isRtl = useMemo(() => subtitlesLanguage?.name === 'Arabic', [subtitlesLanguage]);

  const mergeClipsAndVoiceoverSubtitles = (subtitles) => {
    // To merge the subtitles, define which scenes should be splitted bc of the voiceover
    // Slice the scenes limits, merge sliced scenes, add voiceover and sort by limit start value
    const slicedScenes = [];
    subtitles[CLIPS_TYPE]?.forEach(sceneData => {
      if (sceneData?.overlappedBy?.length) {
        if (sceneData.overlappedBy.find(({ coverage }) => coverage === VOICEOVER_COVERAGE_TYPES.FULL)) {
          return;
        }
        const sliceLimits = [{ start: sceneData.limits.start, end: sceneData.limits.end }];
        sceneData.overlappedBy.forEach(segment => {
          switch (segment.coverage) {
            case VOICEOVER_COVERAGE_TYPES.START:
              sliceLimits[0].start = segment.end;
              break;
            case VOICEOVER_COVERAGE_TYPES.INTERMEDIATE:
              sliceLimits[sliceLimits.length - 1].end = segment.start;
              sliceLimits.push({ start: segment.end, end: sceneData.limits.end });
              break;
            case VOICEOVER_COVERAGE_TYPES.END:
              sliceLimits[sliceLimits.length - 1].end = segment.start;
              break;
            default: break;
          }
        });

        sliceLimits.filter(newLimits => newLimits.start !== newLimits.end).forEach(newLimits => {
          slicedScenes.push({
            ...sceneData, limits: { ...sceneData.limits, ...newLimits }, overlappedBy: null, subtitles: sceneData.subtitles.filter(({ trimmedData = null }) =>
            (trimmedData && (trimmedData.start >= newLimits.start && trimmedData.end <= newLimits.end
              || trimmedData.start <= newLimits.start && trimmedData.end > newLimits.start && trimmedData.end <= newLimits.end
              || trimmedData.start >= newLimits.start && trimmedData.start < newLimits.end && trimmedData.end >= newLimits.end)))
          })
        })
        return;
      }
      slicedScenes.push(sceneData);
    });
    if (subtitles[VOICEOVER_TYPE]) {
      slicedScenes.push(...subtitles[VOICEOVER_TYPE]);
    }
    return slicedScenes.filter(item => item.subtitles.length)?.sort((a, b) => a.limits.start - b.limits.start);
  };

  const selectedSubtitles = useMemo(() => {
    if (!listMode && subtitles[VOICEOVER_TYPE]) {
      return mergeClipsAndVoiceoverSubtitles(subtitles);
    }
    return subtitles[listMode ?? CLIPS_TYPE];
  }, [listMode, mergeClipsAndVoiceoverSubtitles, subtitles])


  const isAllScenesEmpty = useMemo(
    () => selectedSubtitles?.some(item => item.isGenerated) && selectedSubtitles?.map(item => item?.subtitles?.filter(item => item.hasOwnProperty('trimmedData'))).every(item => item.length === 0),
    [selectedSubtitles]
  );

  const handleSwitchToEditor = useCallback((id) => {
    const sceneOrSegment = subtitles[CLIPS_TYPE].find(({ subtitles }) => subtitles.find(({ key }) => key === id));
    setListMode(sceneOrSegment ? CLIPS_TYPE : VOICEOVER_TYPE);
  }, [subtitles]);

  useEffect(() => {
    if (subtitlesInPlayer.filter(x => x?.isChanged).length > 0) {
      setSubtitlesVisibilityChanged(true);
    } else {
      setSubtitlesVisibilityChanged(false);
    }
  }, [subtitlesInPlayer]);

  const savePlayerSubtitles = async () => {
    try {
      const subtitles = subtitlesInPlayer.filter(x => x?.isChanged).map(obj => ({ id: obj.id, visibility: obj.visible }));
      await CmsAPI.updateSubtitleVisibility(video.id, subtitles);
      setSubtitlesInPlayer(subtitlesInPlayer.map(item => ({ ...item, isChanged: false })));
      setShowOption(false);
    } catch (e) {
      console.log('Save Subtitles Languages in Player error', e);
    }
  };

  useEffect(() => {
    if (video?.subtitlesMeta) {
      let visibleSubtitleLanguages = video.visibleSubtitleLanguages
        .reduce((acc, item) => {
          if (!availableLanguages?.some(elem => elem.id === item.language)) {
            return acc;
          }
          const existingItem = acc.find(x => x.language === item.language);

          if (item.enhanced) {
            return [...acc.filter(x => x.language !== item.language), { ...item, isChanged: false }];
          }

          if (!existingItem) {
            return [...acc, { ...item, isChanged: false }];
          }

          return acc;
        }, []);

      console.log('video.visibleSubtitleLanguages', video.visibleSubtitleLanguages);
      if (video.visibleSubtitleLanguages.filter(x => x?.language === subtitlesLanguage.id).length === 0) {
        const subtitleTemp = video.subtitlesMeta.filter(x => x?.language === subtitlesLanguage.id)[0];
        visibleSubtitleLanguages.push({
          id: subtitleTemp?.id,
          language: subtitlesLanguage.id,
          name: subtitlesLanguage.name,
          src: subtitlesLanguage.src,
          visible: subtitleTemp?.visible,
          isChanged: false,
        });
      }
      console.log('subtitlesInPlayer', visibleSubtitleLanguages);
      setSubtitlesInPlayer(visibleSubtitleLanguages);
    }
  }, [video]);

  const handleAddSubtitle = useCallback(({ sceneId, segmentId, id }) => {
    updateSubtitle({ ...(sceneId ? { sceneId } : { segmentId }), id, key: 'add' });
  }, [updateSubtitle]);

  const onSelect = select => {
    onLanguageChange(select);
  };

  const cancelDropDown = () => {
    setShowOption(false);
    setSubtitlesInPlayer(
      subtitlesInPlayer.map(item => (item.isChanged ? { ...item, visible: !item.visible, isChanged: false } : item))
    );
  };

  const toggleSelection = () => setShowOption(prevState => !prevState);
  const selectRef = useRef(null);

  useOutsideClick(selectRef, () => {
    if (showOptions) {
      setShowOption(false);
      cancelDropDown();
    }
  });

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        {!isSearching && (
          <div className={styles.languagesSection}>
            <LanguageItem name={subtitlesLanguage?.name} src={subtitlesLanguage?.src} iconClassName={styles.icon} />

            {mode === EDITOR_MODE.TRANSLATE_SUBTITLES_MODE && (
              <div className={styles.selectLanguagesContainer}>
                <LanguageSelect
                  options={availableLanguages}
                  onSelect={onSelect}
                  currentlySelected={currentLanguage}
                  className={styles.customSelect}
                />
                <div className={styles.subtitleLanguagesContainer}>
                  <p>{t('subtitles.editor.subtitles_in_player')}:</p>
                  <div className={styles.selectWrapper}>
                    <div className={styles.selectInput} onClick={toggleSelection}>
                      <p className={styles.selectTitle}>
                        {amountAvailableLanguages} {t('subtitles.editor.languages')}
                      </p>
                      <div className={styles.buttonWrapper}>
                        <img
                          src={Images.ArrowDownIcon}
                          className={cx(styles.arrowButton, { [styles.turnArrow]: showOptions })}
                          alt={t('accessibility.arrow_down')}
                        />
                      </div>
                    </div>
                    {showOptions && (
                      <div className={styles.customSelectContainer} ref={selectRef}>
                        <div className={styles.customSelectTitle}>{t('subtitles.editor.subtitles_in_player')}</div>
                        <div className={styles.languagesContainer}>
                          {subtitlesInPlayer.map(option => (
                            <SubtitlesLanguageCheckbox
                              key={option.id}
                              option={option}
                              availableLanguages={availableLanguages}
                              subtitlesInPlayer={subtitlesInPlayer}
                              setSubtitlesInPlayer={setSubtitlesInPlayer}
                            />
                          ))}
                        </div>
                        <div className={styles.actionButtons}>
                          <CustomButton
                            variant="secondary"
                            label={t('general.cancel')}
                            className={styles.cancelButton}
                            onClick={cancelDropDown}
                            loadingText={t('general.cancel')}
                          />
                          <CustomButton
                            variant="primary"
                            label={t('general.save')}
                            disabled={!subtitlesVisibilityChanged}
                            onClick={savePlayerSubtitles}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <SearchComponent
          searchValue={searchValue}
          onChange={onSearch}
          className={cx({ [styles.searchSubtitles]: isSearching })}
          inputClassName={styles.searchField}
          searchState={setIsSearching}
          clearButton={true}
          onClose={onSearchClose}
        />
      </div>
      {isVoiceoverSubtitleExist && <SubtitlesViewTabs listMode={listMode} setListMode={setListMode} />}
      <div className={styles.subtitlesContainer}>
        {isAllScenesEmpty ? (
          <div className={styles.noSpeechContainer}>
            <span className={styles.noSpeechLabel}>{t('subtitles.editor.no_speech')}</span>
            {listMode && <CustomButton
              label={t('subtitles.editor.add_subtitle')}
              variant="primary"
              onClick={() => handleAddSubtitle({ sceneId: selectedSubtitles[0]?.sceneId, segmentId: selectedSubtitles[0].segmentId, id: null })}
            />}
          </div>
        ) : (
          selectedSubtitles?.map(({ sceneId = null, segmentId = null, sort, subtitles, isGenerated, limits, overlappedBy = null }, index) => (
            <SubtitlesItemsList
              key={`${sceneId ?? segmentId}_${index}`}
              sceneId={sceneId}
              segmentId={segmentId}
              subtitles={subtitles}
              isGenerated={isGenerated}
              limits={limits}
              sceneOverlappedBy={overlappedBy}
              isLastScene={sort === video?.scenes?.length - 1}
              updateSubtitle={updateSubtitle}
              activeSubtitle={activeSubtitle}
              handleSeekToSubtitle={handleSeekToSubtitle}
              handleAddSubtitle={handleAddSubtitle}
              isTranslation={isTranslation}
              rtl={isRtl}
              searchValue={searchValue}
              readOnly={!listMode}
              handleSwitchToEditor={handleSwitchToEditor}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default SubtitlesEditorComponent;
